import * as Common from "@sp-crm/core";
import { trpcClient } from "components/rpc/trpc";
import { fancyAlert } from "components/ui/fancy-alert";
import { PrimaryButton } from "components/ui/primary-button";
import { Select } from "components/ui/select";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useMemo, useState } from "react";
import { useTenantSettings } from "store/selectors/settings";
import { buildDurationOptions } from "./time-entry-utils";

interface ManualTimeEntryFormProps {
    clientId: Common.ClientId;
    onSaved: () => void;
}

export const ManualTimeEntryForm: React.FC<ManualTimeEntryFormProps> = ({
    clientId,
    onSaved,
}) => {
    const { settings } = useTenantSettings();
    const increment = settings.timeTrackingIncrementMinutes || 15;
    const options = useMemo(() => buildDurationOptions(increment), [increment]);
    const [durationMinutes, setDurationMinutes] = useState(increment);
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    const handleSubmit = useCallback(async () => {
        if (!note.trim()) return;
        setSaving(true);
        try {
            await trpcClient.timeEntries.create.mutate({
                clientId,
                startTime: new Date().toISOString(),
                endTime: null,
                durationMinutes,
                note: note.trim(),
                isManual: true,
            });
            setNote("");
            setDurationMinutes(increment);
            onSaved();
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [clientId, durationMinutes, note, increment, onSaved]);

    return (
        <div className="space-y-4">
            <Select
                label="Duration"
                textSize="sm"
                value={durationMinutes}
                onChange={e => setDurationMinutes(Number(e.target.value))}>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </Select>
            <TextArea
                label="What was done"
                textSize="sm"
                rows={3}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Describe what was done..."
            />
            <PrimaryButton onClick={handleSubmit} disabled={!note.trim() || saving}>
                {saving ? "Saving..." : "Save"}
            </PrimaryButton>
        </div>
    );
};
